import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useUserLoginMutation,
  useLazyGetUserInfoQuery,
  useCreateUserMutation,
  useCaptureSignupAttributionMutation,
} from '@features/user';
import { Box, CircularProgress } from '@mui/material';
import SentryService from '../services/SentryService';
import { UTMTracker } from '@services/utm';
import { fireBrowserSignupPixels, readJpAttributionCookie } from '@services/attribution';
import { FRONTEND_APP_VERSION } from '../constants';
import { Auth0Initializer } from '../components/Auth0Initializer';
import { setLastUserLogin, type LastUserLogin } from '../types/auth';
import { AccountBlockedError } from '../components/errors/AccountBlockedError';

export type UserDataType = any;

export const AuthenticationCallbackWrapper = () => {
  // production
  const domain = 'smartscribehealth.us.auth0.com';
  const clientId = 'RG4FwiozZWwAAe83xQxv2metpgJaPe0j';
  const sharedCallback = import.meta.env.VITE_SHARED_AUTH_CALLBACK;
  const redirectUri = sharedCallback
    ? window.location.origin + sharedCallback
    : window.location.origin + '/callback';
  const audience = 'https://api.smartscribe.health';

  // development
  // const domain = "smartscribe-development.us.auth0.com";
  // const clientId = "ohWExKeDUG8L1LY5oRJXPseSfaqN1mid";
  // const redirectUri = window.location.origin + "/callback";
  // const audience = "https://api.smartscribe.health/development";

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience: audience,
        redirect_uri: redirectUri,
        scope: 'openid profile email offline_access',
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <Auth0Initializer>
        <AuthenticationCallback />
      </Auth0Initializer>
    </Auth0Provider>
  );
};

const AuthenticationCallback = () => {
  const { error, getAccessTokenSilently, user } = useAuth0();
  const [userLogin] = useUserLoginMutation();
  const [getUserInfo] = useLazyGetUserInfoQuery();
  const [createUser] = useCreateUserMutation();
  const [captureSignupAttribution] = useCaptureSignupAttributionMutation();
  const navigate = useNavigate();
  const loginInProgressRef = useRef(false);
  const callbackInitRef = useRef(false);

  // Function to append parameters from localStorage to a URL
  const appendParamsFromLocalStorage = (url: string) => {
    const paramsJson = localStorage.getItem('urlParams');
    if (!paramsJson) return url;

    const paramsObj = JSON.parse(paramsJson);
    if (Object.keys(paramsObj).length === 0) return url;

    const urlObj = new URL(url, window.location.origin);

    Object.keys(paramsObj).forEach(key => {
      urlObj.searchParams.set(key, paramsObj[key]);
    });

    localStorage.removeItem('urlParams');

    return urlObj.toString();
  };

  const saveAccessToken = async () => {
    try {
      console.log('Attempting to get access token...');
      const accessToken = await getAccessTokenSilently();
      console.log('Got access token:', !!accessToken);
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
      }
    } catch (err) {
      console.error('Token error:', err);
    }
  };

  const updateUserLogins = async () => {
    // Use ref for atomic check-and-set to prevent race conditions
    if (loginInProgressRef.current) {
      console.log('Login already in progress, skipping...');
      return;
    }

    // Check session storage as secondary protection against double-processing
    const callbackLoginAttempt = sessionStorage.getItem('auth_callback_login_attempted');
    if (callbackLoginAttempt === 'true') {
      // Get returnTo from localStorage, or fall back to sessionStorage backup
      const returnTo =
        localStorage.getItem('returnTo') || sessionStorage.getItem('auth_callback_destination');
      if (returnTo && returnTo !== '/') {
        // We have a destination - navigate to it
        const urlWithParams = appendParamsFromLocalStorage(returnTo);
        const url = new URL(urlWithParams, window.location.origin);
        const path = url.pathname + url.search + url.hash;
        localStorage.removeItem('returnTo');
        navigate(path);
      } else {
        // No destination saved - just go to dashboard
        navigate('/');
      }
      return;
    }

    // Atomically set the flag
    loginInProgressRef.current = true;
    sessionStorage.setItem('auth_callback_login_attempted', 'true');

    // Compute destination before the network call so we can navigate even if userLogin fails.
    const returnTo = localStorage.getItem('returnTo') || '/';
    const urlWithParams = appendParamsFromLocalStorage(returnTo);
    const url = new URL(urlWithParams, window.location.origin);
    const path = url.pathname + url.search + url.hash;

    try {
      await userLogin({
        device_info: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          platform: 'web',
          userAgent: navigator.userAgent,
          appVersion: FRONTEND_APP_VERSION,
          hostname: window.location.hostname,
          screen: { height: screen.height, width: screen.width },
        } as any, // Type assertion because the type definition is too restrictive
      }).unwrap();
    } catch (error) {
      // userLogin is an informational beacon — failures shouldn't trap the user on /callback.
      // Fall through and still navigate.
      console.error('User login failed:', error);
      SentryService.captureException(error, {
        level: 'warning',
        tags: { component: 'AuthenticationCallback', action: 'userLogin' },
      });
    }

    // Save destination to sessionStorage as backup in case callback runs again,
    // clear returnTo from localStorage, then navigate.
    sessionStorage.setItem('auth_callback_destination', path);
    localStorage.removeItem('returnTo');
    navigate(path);
  };

  const attemptCreateUser = async () => {
    if (!user?.email) return;
    try {
      const inviteToken = localStorage.getItem('invite_token') || undefined;
      const utm = UTMTracker.getUTMsForAPI();
      await createUser({
        createUserRequest: {
          email: user.email,
          invite_token: inviteToken,
          platform: 'web',
          utm: Object.keys(utm).length > 0 ? utm : undefined,
        },
      }).unwrap();
    } catch (error: any) {
      const status = error?.status || error?.data?.status;
      if (status === 409) return; // User exists — normal login, not an error
      console.error('Atomic user creation failed:', error);
      SentryService.captureException(error, {
        level: 'warning',
        tags: { component: 'AuthenticationCallback', action: 'createUser' },
      });
      // Non-blocking: old flow via updateUserInfo will still work as fallback
    }
  };

  // gdn-1180: read jp_attribution cookie, hand it to the BE for persistence +
  // server-side GA4 MP / Meta CAPI fires, and — only on first capture — fire
  // browser-side pixels with the same event_id for Meta's pixel/CAPI dedupe.
  // Idempotent server-side on user_id; safe to call on every callback.
  const captureAttribution = async () => {
    const cookie = readJpAttributionCookie();
    const eventId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      const result = await captureSignupAttribution({
        cookie_payload: (cookie as Record<string, unknown> | null) ?? null,
        event_id: eventId,
      }).unwrap();

      if (result.first_capture) {
        fireBrowserSignupPixels({ eventId, cookie });
      }
    } catch (error: any) {
      // Do NOT fire browser pixels here. Without a successful BE persist there is
      // no CAPI counterpart for Meta to dedupe against, so a browser-only fire
      // pollutes CompleteRegistration with undeduped events (every retry/failure
      // for a returning user becomes another sign_up). The next successful
      // capture will fire the pixel once with first_capture=true.
      SentryService.captureException(error, {
        level: 'warning',
        tags: { component: 'AuthenticationCallback', action: 'captureSignupAttribution' },
      });
    }
  };

  const saveSmartLoginInfo = () => {
    // Save user info for smart login on next visit. Doesn't depend on backend response.
    if (user?.email) {
      const lastUserLogin: LastUserLogin = {
        email: user.email,
        name: user.name,
        picture: user.picture,
        connection: user.sub?.split('|')[0] || 'auth0',
      };
      setLastUserLogin(lastUserLogin);
    }
  };

  const saveUserInfo = async () => {
    try {
      await getUserInfo(undefined);
      // User info is stored in RTK Query cache automatically.
    } catch (error) {
      console.error('Failed to get user info:', error);
      SentryService.captureException(error, {
        level: 'warning',
        tags: { component: 'AuthenticationCallback', action: 'getUserInfo' },
      });
    }
  };

  useEffect(() => {
    if (!user) return;
    // Ref guard prevents duplicate calls on re-renders
    if (callbackInitRef.current) return;
    callbackInitRef.current = true;

    const run = async () => {
      saveAccessToken();
      saveSmartLoginInfo();

      // Each step has its own try/catch and shouldn't throw, but await sequentially
      // so updateUserLogins always runs last regardless of upstream failures.
      // Prevents users from getting stuck on /callback when an upstream step fails
      // (e.g., captureAttribution 500s, getUserInfo errors).
      await attemptCreateUser();
      await captureAttribution();
      await saveUserInfo();
      await updateUserLogins();
    };

    void run();
  }, [user]);

  // Add a timeout to refresh the page if the redirect doesn't happen within 10 seconds
  // Skip if there's an error - no point in refreshing when showing error page
  useEffect(() => {
    if (error) {
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    // Get current refresh count from sessionStorage or initialize to 0
    const refreshCount = parseInt(sessionStorage.getItem('auth_refresh_count') || '0');

    if (refreshCount < 3) {
      timeoutId = setTimeout(() => {
        console.log(
          `Timeout reached: Refreshing the page (attempt ${
            refreshCount + 1
          } of 3) as redirect didn't happen`,
        );
        // Increment the refresh count
        sessionStorage.setItem('auth_refresh_count', (refreshCount + 1).toString());
        window.location.reload();
      }, 10000); // 10 seconds timeout
    } else {
      console.log('Maximum refresh attempts (3) reached. Not refreshing anymore.');
    }

    // Cleanup function that always runs
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Clean up session storage when component unmounts after max attempts
      if (refreshCount >= 3) {
        sessionStorage.removeItem('auth_refresh_count');
        sessionStorage.removeItem('auth_callback_login_attempted');
      }
    };
  }, [error]);

  // Handle error case after all hooks
  if (error) {
    // Check if this is a blocked user error from Auth0
    // Auth0 returns: error=unauthorized&error_description=user%20is%20blocked
    const errorMessage = error.message?.toLowerCase() || '';
    const isBlockedUser =
      errorMessage.includes('blocked') || errorMessage.includes('user is blocked');

    if (isBlockedUser) {
      return <AccountBlockedError />;
    }

    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2, overflowWrap: 'anywhere' }}>{error.message}</Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: '100dvh', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <CircularProgress />
        {parseInt(sessionStorage.getItem('auth_refresh_count') || '0') >= 3 && (
          <Box sx={{ mt: 3, textAlign: 'center', maxWidth: '80%' }}>
            <p>
              Authentication is taking longer than expected. Please try refreshing the page manually
              or contact support if the issue persists.
            </p>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AuthenticationCallback;
